import { toast } from "@/lib/toast";
import type { 
  ExecutionEvent, 
  ExecutionNotification, 
  ExecutionNotificationPreferences,
  ExecutionStatus,
  ExecutionMethod 
} from '../types/execution.types';

export class ExecutionNotificationService {
  private preferences: ExecutionNotificationPreferences = {
    enableExecutionAlerts: true,
    enableFailureAlerts: true,
    enableConfigurationAlerts: true,
    enableSummaryAlerts: true,
    soundEnabled: true,
    doNotDisturbMode: false,
    doNotDisturbStart: '22:00',
    doNotDisturbEnd: '08:00',
    criticalOnly: false,
    maxNotificationsPerHour: 10,
    notificationHistory: []
  };

  private notificationCount = 0;
  private lastHourReset = Date.now();

  /**
   * Load notification preferences from localStorage
   */
  loadPreferences(): ExecutionNotificationPreferences {
    try {
      const stored = localStorage.getItem('execution-notification-preferences');
      if (stored) {
        this.preferences = { ...this.preferences, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    }
    return this.preferences;
  }

  /**
   * Save notification preferences to localStorage
   */
  savePreferences(preferences: Partial<ExecutionNotificationPreferences>): void {
    try {
      this.preferences = { ...this.preferences, ...preferences };
      localStorage.setItem('execution-notification-preferences', JSON.stringify(this.preferences));
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    }
  }

  /**
   * Check if notifications are currently disabled due to do-not-disturb
   */
  private isDoNotDisturbActive(): boolean {
    if (!this.preferences.doNotDisturbMode) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMinute] = this.preferences.doNotDisturbStart.split(':').map(Number);
    const [endHour, endMinute] = this.preferences.doNotDisturbEnd.split(':').map(Number);
    
    const startTime = startHour * 60 + startMinute;
    const endTime = endHour * 60 + endMinute;

    // Handle overnight do-not-disturb periods
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    }
    
    return currentTime >= startTime && currentTime <= endTime;
  }

  /**
   * Check if notification rate limit has been exceeded
   */
  private isRateLimited(): boolean {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    // Reset counter if more than an hour has passed
    if (now - this.lastHourReset > oneHour) {
      this.notificationCount = 0;
      this.lastHourReset = now;
    }

    return this.notificationCount >= this.preferences.maxNotificationsPerHour;
  }

  /**
   * Play notification sound if enabled
   */
  private playNotificationSound(type: 'success' | 'error' | 'warning' | 'info'): void {
    if (!this.preferences.soundEnabled) return;

    try {
      // Create audio context for notification sounds
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Different frequencies for different notification types
      const frequencies = {
        success: 800,
        error: 300,
        warning: 600,
        info: 500
      };

      const frequency = frequencies[type];
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  }

  /**
   * Add notification to history
   */
  private addToHistory(notification: ExecutionNotification): void {
    this.preferences.notificationHistory.unshift({
      ...notification,
      timestamp: new Date().toISOString(),
      read: false
    });

    // Keep only last 100 notifications
    if (this.preferences.notificationHistory.length > 100) {
      this.preferences.notificationHistory = this.preferences.notificationHistory.slice(0, 100);
    }

    // Save updated preferences
    this.savePreferences({ notificationHistory: this.preferences.notificationHistory });
  }

  /**
   * Handle execution status notifications
   */
  handleExecutionEvent(event: ExecutionEvent): void {
    if (!this.preferences.enableExecutionAlerts) return;

    const notification: ExecutionNotification = {
      id: `execution-${event.executionId}-${Date.now()}`,
      type: 'execution',
      title: this.getExecutionTitle(event),
      message: this.getExecutionMessage(event),
      severity: this.getExecutionSeverity(event.status),
      timestamp: new Date().toISOString(),
      read: false,
      executionId: event.executionId
    };

    this.showNotification(notification);
  }

  /**
   * Handle execution failure notifications
   */
  handleExecutionFailure(executionId: string, method: ExecutionMethod, error: string): void {
    if (!this.preferences.enableFailureAlerts) return;

    const notification: ExecutionNotification = {
      id: `failure-${executionId}-${Date.now()}`,
      type: 'failure',
      title: 'Execution Failed',
      message: `${method} execution failed: ${error}`,
      severity: 'error',
      timestamp: new Date().toISOString(),
      read: false,
      executionId
    };

    this.showNotification(notification);
  }

  /**
   * Handle configuration change notifications
   */
  handleConfigurationChange(message: string): void {
    if (!this.preferences.enableConfigurationAlerts) return;

    const notification: ExecutionNotification = {
      id: `config-${Date.now()}`,
      type: 'configuration',
      title: 'Configuration Updated',
      message,
      severity: 'info',
      timestamp: new Date().toISOString(),
      read: false
    };

    this.showNotification(notification);
  }

  /**
   * Handle execution summary notifications
   */
  handleExecutionSummary(summary: string): void {
    if (!this.preferences.enableSummaryAlerts) return;

    const notification: ExecutionNotification = {
      id: `summary-${Date.now()}`,
      type: 'summary',
      title: 'Execution Summary',
      message: summary,
      severity: 'info',
      timestamp: new Date().toISOString(),
      read: false
    };

    this.showNotification(notification);
  }

  /**
   * Show notification using toast system
   */
  private showNotification(notification: ExecutionNotification): void {
    // Check if we should show this notification
    if (this.isDoNotDisturbActive() && notification.severity !== 'error') return;
    if (this.isRateLimited() && notification.severity !== 'error') return;
    if (this.preferences.criticalOnly && notification.severity !== 'error') return;

    // Add to history
    this.addToHistory(notification);

    // Increment notification count
    this.notificationCount++;

    // Play sound
    this.playNotificationSound(notification.severity === 'error' ? 'error' : 
                              notification.severity === 'warning' ? 'warning' : 
                              notification.severity === 'success' ? 'success' : 'info');

    // Show toast notification
    const toastOptions = {
      id: notification.id,
      description: notification.message,
      duration: notification.severity === 'error' ? 0 : 5000, // Errors stay until dismissed
      action: notification.executionId ? {
        label: 'View Details',
        onClick: () => this.handleViewDetails(notification.executionId!)
      } : undefined
    };

    switch (notification.severity) {
      case 'error':
        toast.error(notification.title, toastOptions);
        break;
      case 'warning':
        toast.warning(notification.title, toastOptions);
        break;
      case 'success':
        toast.success(notification.title, toastOptions);
        break;
      case 'info':
      default:
        toast(notification.title, toastOptions);
        break;
    }
  }

  /**
   * Handle view details action
   */
  private handleViewDetails(executionId: string): void {
    // This would typically navigate to execution details
    // For now, we'll just log it
    console.log('View execution details:', executionId);
    // In a real implementation, this would trigger navigation
    // router.push(`/execution/details/${executionId}`);
  }

  /**
   * Get execution title based on event
   */
  private getExecutionTitle(event: ExecutionEvent): string {
    switch (event.status) {
      case 'pending':
        return 'Execution Queued';
      case 'executing':
        return 'Execution Started';
      case 'completed':
        return 'Execution Completed';
      case 'failed':
        return 'Execution Failed';
      case 'cancelled':
        return 'Execution Cancelled';
      default:
        return 'Execution Update';
    }
  }

  /**
   * Get execution message based on event
   */
  private getExecutionMessage(event: ExecutionEvent): string {
    const methodName = event.method.charAt(0).toUpperCase() + event.method.slice(1);
    
    switch (event.status) {
      case 'pending':
        return `${methodName} execution queued for ${event.symbol}`;
      case 'executing':
        return `${methodName} execution started for ${event.symbol}`;
      case 'completed':
        return `${methodName} execution completed for ${event.symbol}`;
      case 'failed':
        return `${methodName} execution failed for ${event.symbol}`;
      case 'cancelled':
        return `${methodName} execution cancelled for ${event.symbol}`;
      default:
        return `${methodName} execution update for ${event.symbol}`;
    }
  }

  /**
   * Get execution severity based on status
   */
  private getExecutionSeverity(status: ExecutionStatus): 'info' | 'success' | 'warning' | 'error' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      case 'executing':
        return 'info';
      case 'pending':
      default:
        return 'info';
    }
  }

  /**
   * Get notification history
   */
  getNotificationHistory(): ExecutionNotification[] {
    return this.preferences.notificationHistory;
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    const notification = this.preferences.notificationHistory.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.savePreferences({ notificationHistory: this.preferences.notificationHistory });
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.preferences.notificationHistory.forEach(n => n.read = true);
    this.savePreferences({ notificationHistory: this.preferences.notificationHistory });
  }

  /**
   * Clear notification history
   */
  clearHistory(): void {
    this.preferences.notificationHistory = [];
    this.savePreferences({ notificationHistory: [] });
  }

  /**
   * Get current preferences
   */
  getPreferences(): ExecutionNotificationPreferences {
    return { ...this.preferences };
  }
}

// Export singleton instance
export const executionNotificationService = new ExecutionNotificationService();